import {
  ArrowLeft2,
  ArrowRight2,
  Edit,
  Refresh,
  SearchNormal,
  Sort,
  Trash,
} from "iconsax-react";
import { useEffect, useState } from "react";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import AddPatientModal from "../../../components/modals/AddPatientModal";
import {
  Link,
  useLoaderData,
  useLocation,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { PaginationButton } from "../../../components/PaginationButton";
import EditPatientModal from "../../../components/modals/EditPatientModal";
import { ConfirmationModal } from "../../../components/modals/doctor/ConfirmationModal";
import { toast } from "react-toastify";

export const patientsLoader = async ({ params }) => {
  const token = getToken();
  const data = await instance.get("/doctor/patient/all", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

export default function PatientsPage() {
  const response = useLoaderData();
  const [patientList, setPatientList] = useState(
    [...response.data].map((_, index) => ({ ..._, index: index + 1 }))
  );
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [updatePatient, setUpdatePatient] = useState(null);
  const [filteredList, setFilteredList] = useState(
    patientList.filter((_, index) => index + 1 <= 10)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const [confirmDeletePatient, setConfirmDeletePatient] = useState(null);
  const [loading, setLoading] = useState(false)
  const revalidator = useRevalidator();

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredList(() => {
      return patientList
        .filter((patient) => {
          const id = `${patient.id}`;
          const firstName = patient.firstname.toLowerCase().includes(query);
          const lastName = patient.lastname.toLowerCase().includes(query);
          const middleName = patient.middlename.toLowerCase().includes(query);
          const email = patient.email_address.toLowerCase().includes(query);

          return id === query || firstName || lastName || middleName || email;
        })
        .filter((_, index) => index + 1 <= 10);
    });
  };

  useEffect(() => {
    setPatientList(
      [...response.data].map((_, index) => ({ ..._, index: index + 1 }))
    );
  }, [response.data]);

  useEffect(() => {
    setFilteredList(() =>
      patientList.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );
  }, [pageIndex, patientList]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }
        patientList.reverse();
        setPatientList(patientList);
        setFilteredList((items) =>
          patientList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "name": {
        setPageIndex(1);
        if (sorted === "name") {
          setSorted("");
          patientList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("name");
          patientList.sort((a, b) => {
            if (a.lastname < b.lastname) return -1;
            if (a.lastname > b.lastname) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          patientList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "status": {
        setPageIndex(1);
        if (sorted === "status") {
          setSorted("");
          patientList.sort((a, b) => a.index - b.index);
        } else {
          setSorted("status");
          patientList.sort((a, b) => {
            if (a.account_status < b.account_status) return -1;
            if (a.account_status > b.account_status) return 1;
            return 0;
          });
        }
        setFilteredList((items) =>
          patientList.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
    }
  };

  const handleDeletePatient = async (patient) => {
    try {
      setLoading(true);
      const token = getToken();
      await instance.delete(`/doctor/patient/${patient.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Patient deleted');
      revalidator.revalidate();
      setLoading(false);
      setConfirmDeletePatient(null);
    } catch (error) {
      setLoading(false);
      toast.error(
        error.response ? error.response.data.message : "An error occurred"
      );
    }
  };

  return (
    <>
      <div className="bg-[#fafafa] max-w-[calc(100%_-_100px)] lg:max-w-[100%] w-full px-7 py-5">
        <AddPatientModal
          isOpen={isAddPatientModalOpen}
          onClose={() => setIsAddPatientModalOpen(false)}
          triggerRevalidate={() => (location.href = "/clinic/dashboard/patients")}
        />
        {updatePatient && (
          <EditPatientModal
            patient={updatePatient}
            onClose={() => setUpdatePatient(null)}
            triggerRevalidate={() => (location.href = "/clinic/dashboard/patients")}
          />
        )}

        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold">
            All Patients ({patientList.length})
          </div>

          <div className="flex gap-2 items-center">
            <button
              onClick={() => location.reload()}
              className="bg-[#00b0ad0d] py-2 px-3 text-primary flex gap-2 items-center rounded-md font-medium text-[14px] hover:opacity-80"
            >
              Refresh <Refresh size={16} />
            </button>
            <button
              onClick={() => setIsAddPatientModalOpen(true)}
              className="bg-primary py-2 px-3 shadow-sm rounded-md font-medium text-[14px] hover:opacity-80 text-white"
            >
              Add Patient +
            </button>
          </div>
        </div>

        <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
          <div className="flex justify-between p-2 items-center">
            <form className="relative" onSubmit={handleSearch}>
              <input
                className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder:font-normal placeholder:text-[14px] text-sm"
                placeholder="Search patient by name/email/id"
                name="query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
                type="submit"
              >
                <SearchNormal size={15} />
              </button>
            </form>

            <div className="flex gap-2 items-center">
              <PaginationButton
                item={<ArrowLeft2 size={16} />}
                disabled={pageIndex <= 1}
                handleClick={() => setPageIndex(pageIndex - 1)}
              />
              <span className="font-light text-xs">Page {pageIndex}</span>
              <PaginationButton
                item={<ArrowRight2 size={16} />}
                disabled={pageIndex >= Math.ceil(patientList.length / 10)}
                handleClick={() => setPageIndex((index) => index + 1)}
              />
            </div>
          </div>

          <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
            <table className="w-full relative whitespace-nowrap">
              <thead>
                <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                  <td
                    className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                    onClick={() => handleSort("index")}
                  >
                    #{" "}
                    <Sort
                      size="15"
                      className={`${
                        sorted === "index" ? "rotate-180" : "rotate-0"
                      } inline`}
                      color="#FF8A65"
                    />
                  </td>
                  <td className="px-6 py-4">Patient ID</td>
                  <td
                    className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                    onClick={() => handleSort("name")}
                  >
                    Fullname{" "}
                    <Sort
                      size="15"
                      className={`${
                        sorted === "name" ? "rotate-180" : "rotate-0"
                      } inline`}
                      color="#FF8A65"
                    />
                  </td>
                  <td className="px-6 py-4">S. Practitioner</td>
                  <td
                    className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                    onClick={() => handleSort("status")}
                  >
                    Status{" "}
                    <Sort
                      size="15"
                      className={`${
                        sorted === "status" ? "rotate-180" : "rotate-0"
                      } inline`}
                      color="#FF8A65"
                    />
                  </td>
                  <td className="px-6 py-4">Email</td>
                  <td className="px-6 py-4"></td>
                  <td className="px-6 py-4"></td>
                </tr>
              </thead>

              <tbody>
                {filteredList.length < 1 ? (
                  <tr>
                    <td className="px-6 py-4 font-normal" colSpan={6}>
                      You do not have any patient yet
                    </td>
                  </tr>
                ) : (
                  filteredList.map((patient, index) => (
                    <tr
                      key={patient.index}
                      className="h-14 text-[14px] font-normal border-b"
                    >
                      <td className="px-6 py-4">{patient.index}</td>
                      <td className="px-6 py-4">
                        <Link
                          to={`${patient.id}`}
                          className="underline text-primary"
                        >
                          {patient.id}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        {patient.lastname} {patient.middlename}{" "}
                        {patient.firstname}
                      </td>
                      <td className="px-6 py-4">
                        {patient.doctor ? `${patient.doctor?.prefix}.` : ""}{" "}
                        {patient.doctor?.lastname} {patient.doctor?.firstname}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`${
                            patient.account_status === "inactive"
                              ? "bg-red-100"
                              : "bg-green-100"
                          } max-w-[100px] w-full text-center inline-block capitalize py-[2px] px-2 rounded-md`}
                        >
                          {patient.account_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">{patient.email_address}</td>
                      <td className="px-6 py-4 underline cursor-pointer duration-300 hover:text-primary">
                        <Edit
                          onClick={() => {
                            setUpdatePatient(patient);
                          }}
                          size={24}
                        />
                      </td>
                      <td className="px-6 py-4 underline cursor-pointer duration-300 hover:text-[tomato]">
                        <Trash
                          onClick={() => {
                            setConfirmDeletePatient(patient);
                          }}
                          size={24}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {confirmDeletePatient && (
        <>
          <ConfirmationModal
            onClose={() => setConfirmDeletePatient(null)}
            cta={() => handleDeletePatient(confirmDeletePatient)}
            msg={"Are you sure you want to delete patient?"}
            ctaLoading={loading}
          />
          <div
            className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
            onClick={() => setConfirmDeletePatient(null)}
          />
        </>
      )}
    </>
  );
}
